import { useEffect, useState } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { useCourses, type Course, type CoursePurchase } from '@/hooks/useCourses'
import { EVALUATION_ASPECTS } from '@/lib/evaluationAspects'
import {Plus, Pencil, Trash2, Eye, EyeOff, BookOpen, Users, CheckCircle, Upload, X, Save, DollarSign, Tag, FileText, Image as ImageIcon, BadgeCheck, Clock, AlertCircle, Check, Ban, } from 'lucide-react'
import { lumi } from '@/lib/lumi'

const TABS = ['Cursos', 'Inscritos', 'Completados', 'Pagos pendientes', 'Historial de pagos'] as const
type Tab = typeof TABS[number]

const TYPE_LABELS: Record<string, string> = { free: 'Gratuito', premium: 'Premium' }
const STATUS_LABELS: Record<string, string> = { active: 'Activo', inactive: 'Inactivo' }
const PAY_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Pagado', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
}

const emptyForm = {
  title: '',
  description: '',
  skill: '',
  skillLabel: '',
  type: 'free' as 'free' | 'premium',
  price: '',
  status: 'active' as 'active' | 'inactive',
  htmlContent: '',
  coverUrl: '',
  relatedPremiumId: '',
}

export default function AdminCursos() {
  const {
    courses, purchases, progress,
    loading,
    fetchAllCourses, fetchPurchases, fetchAllProgress,
    createCourse, updateCourse, deleteCourse, updatePurchase,
  } = useCourses()

  const [tab, setTab] = useState<Tab>('Cursos')
  const [payFilter, setPayFilter] = useState<'Todos' | 'Pendiente' | 'Pagado' | 'Rechazado'>('Todos')
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Course | null>(null)
  const [form, setForm] = useState({ ...emptyForm })
  const [saving, setSaving] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [viewCourse, setViewCourse] = useState<Course | null>(null)
  const [viewPurchase, setViewPurchase] = useState<CoursePurchase | null>(null)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    fetchAllCourses()
    fetchPurchases()
    fetchAllProgress()
  }, [])

  const set = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }))

  const openNew = () => {
    setEditing(null)
    setForm({ ...emptyForm })
    setShowForm(true)
  }

  const openEdit = (c: Course) => {
    setEditing(c)
    setForm({
      title: c.title,
      description: c.description ?? '',
      skill: c.skill,
      skillLabel: c.skillLabel ?? '',
      type: c.type,
      price: c.price != null ? String(c.price) : '',
      status: c.status,
      htmlContent: c.htmlContent ?? '',
      coverUrl: c.coverUrl ?? '',
      relatedPremiumId: c.relatedPremiumId ?? '',
    })
    setShowForm(true)
  }

  const handleSkillChange = (key: string) => {
    const asp = EVALUATION_ASPECTS.find(a => a.key === key)
    setForm(p => ({ ...p, skill: key, skillLabel: asp?.label ?? key }))
  }

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingCover(true)
    try {
      const [res] = await lumi.tools.file.upload([file])
      set('coverUrl', res.fileUrl)
    } catch {
      alert('Error al subir la imagen')
    } finally {
      setUploadingCover(false)
    }
  }

  const handleSave = async () => {
    if (!form.title.trim() || !form.skill) {
      alert('El nombre y la habilidad son obligatorios.')
      return
    }
    setSaving(true)
    try {
      const user = lumi.auth.user
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        skill: form.skill,
        skillLabel: form.skillLabel,
        type: form.type,
        price: form.type === 'premium' && form.price ? parseFloat(form.price) : 0,
        status: form.status,
        htmlContent: form.htmlContent.trim(),
        coverUrl: form.coverUrl,
        relatedPremiumId: form.relatedPremiumId,
        creator: user?.userId ?? '',
      }
      if (editing) {
        await updateCourse(editing._id, payload)
        setMsg('Curso actualizado correctamente.')
      } else {
        await createCourse(payload)
        setMsg('Curso creado correctamente.')
      }
      setShowForm(false)
      await fetchAllCourses()
      setTimeout(() => setMsg(''), 3000)
    } catch {
      alert('Error al guardar el curso.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteCourse(id)
      await fetchAllCourses()
      setConfirmDelete(null)
      setMsg('Curso eliminado.')
      setTimeout(() => setMsg(''), 3000)
    } catch {
      alert('Error al eliminar el curso.')
    }
  }

  const handleValidatePurchase = async (purchase: CoursePurchase, newStatus: 'paid' | 'rejected') => {
    try {
      const user = lumi.auth.user
      await updatePurchase(purchase._id, {
        status: newStatus,
        validatedAt: new Date().toISOString(),
        validatedBy: user?.userId ?? '',
      })
      // Notificar al colaborador sobre el resultado del pago
      const notifTitle = newStatus === 'paid'
        ? `Tu acceso al curso fue desbloqueado`
        : `Pago rechazado`
      const notifBody = newStatus === 'paid'
        ? `Tu pago por transferencia para el curso "${purchase.courseTitle ?? purchase.courseId}" ha sido validado. Ya tienes acceso completo al curso.`
        : `Tu solicitud de pago para el curso "${purchase.courseTitle ?? purchase.courseId}" fue rechazada. Contáctanos si crees que es un error.`
      await lumi.entities.notifications.create({
        title: notifTitle,
        body: notifBody,
        type: 'curso',
        targetAudience: 'colaboradores',
        targetUserId: purchase.collaboratorUserId,
        status: 'publicada',
        linkUrl: '/colaborador/cursos',
        creator: user?.userId ?? 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      await fetchPurchases()
      setViewPurchase(null)
      setMsg(newStatus === 'paid' ? 'Pago validado. Curso desbloqueado y colaborador notificado.' : 'Pago rechazado. Colaborador notificado.')
      setTimeout(() => setMsg(''), 4000)
    } catch {
      alert('Error al actualizar el pago.')
    }
  }

  // Cursos Premium (para selector de relación)
  const premiumCourses = courses.filter(c => c.type === 'premium' && c.status === 'active')

  // Stats
  const totalEnrolled = progress.length
  const totalCompleted = progress.filter(p => p.status === 'completed').length
  const pendingPurchases = purchases.filter(p => p.status === 'pending')
  const allPurchases = purchases

  const STATUS_FILTER_MAP: Record<string, string> = { 'Pendiente': 'pending', 'Pagado': 'paid', 'Rechazado': 'rejected' }
  const filteredPurchaseHistory = payFilter === 'Todos'
    ? allPurchases
    : allPurchases.filter(p => p.status === STATUS_FILTER_MAP[payFilter])

  const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400'
  const labelCls = 'block text-xs font-semibold text-gray-600 mb-1'

  return (
    <AdminLayout title="Cursos" subtitle="Gestión de cursos de desarrollo profesional">
      {/* Mensaje de éxito */}
      {msg && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm font-medium text-green-700 border border-green-200">
          <CheckCircle size={16} />
          {msg}
        </div>
      )}

      {/* KPIs */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total cursos', value: courses.length, icon: BookOpen, color: 'bg-blue-50 text-blue-600' },
          { label: 'Inscritos', value: totalEnrolled, icon: Users, color: 'bg-purple-50 text-purple-600' },
          { label: 'Completados', value: totalCompleted, icon: CheckCircle, color: 'bg-green-50 text-green-600' },
          { label: 'Pagos pendientes', value: pendingPurchases.length, icon: Clock, color: 'bg-amber-50 text-amber-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl bg-white p-4 shadow-sm border border-gray-100">
            <div className={`mb-2 inline-flex rounded-lg p-2 ${color}`}>
              <Icon size={18} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs + botón crear */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-xl bg-gray-100 p-1">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t}
              {t === 'Pagos pendientes' && pendingPurchases.length > 0 && (
                <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
                  {pendingPurchases.length}
                </span>
              )}
            </button>
          ))}
        </div>
        {tab === 'Cursos' && (
          <button
            onClick={openNew}
            className="flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 transition-colors"
          >
            <Plus size={16} />
            Crear curso
          </button>
        )}
      </div>

      {/* ── TAB: CURSOS ── */}
      {tab === 'Cursos' && (
        <div className="space-y-3">
          {loading && <p className="text-sm text-gray-400">Cargando...</p>}
          {!loading && courses.length === 0 && (
            <div className="rounded-xl bg-white border border-dashed border-gray-200 p-10 text-center">
              <BookOpen size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-400">No hay cursos creados aún.</p>
            </div>
          )}
          {courses.map(c => {
            const enrolled = progress.filter(p => p.courseId === c._id).length
            const completed = progress.filter(p => p.courseId === c._id && p.status === 'completed').length
            return (
              <div key={c._id} className="flex items-start gap-4 rounded-xl bg-white border border-gray-100 p-4 shadow-sm">
                {/* Portada */}
                <div className="h-16 w-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                  {c.coverUrl
                    ? <img src={c.coverUrl} alt={c.title} className="h-full w-full object-cover" />
                    : <div className="flex h-full w-full items-center justify-center"><BookOpen size={24} className="text-gray-300" /></div>
                  }
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${c.type === 'premium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                      {TYPE_LABELS[c.type]}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {STATUS_LABELS[c.status]}
                    </span>
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">
                      {c.skillLabel ?? c.skill}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm truncate">{c.title}</h3>
                  <p className="text-xs text-gray-400 truncate">{c.description}</p>
                  <div className="mt-2 flex gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Users size={12} /> {enrolled} inscritos</span>
                    <span className="flex items-center gap-1"><CheckCircle size={12} /> {completed} completados</span>
                    {c.type === 'premium' && <span className="flex items-center gap-1"><DollarSign size={12} /> ${c.price?.toLocaleString('es-MX')} MXN</span>}
                  </div>
                </div>
                {/* Acciones */}
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => setViewCourse(c)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-700" title="Ver contenido">
                    <Eye size={15} />
                  </button>
                  <button onClick={() => openEdit(c)} className="rounded-lg p-1.5 text-blue-400 hover:bg-blue-50 hover:text-blue-700" title="Editar">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => setConfirmDelete(c._id)} className="rounded-lg p-1.5 text-red-400 hover:bg-red-50 hover:text-red-700" title="Eliminar">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── TAB: INSCRITOS ── */}
      {tab === 'Inscritos' && (
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Colaborador', 'Curso', 'Habilidad', 'Tipo', 'Estado', 'Fecha'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {progress.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Sin registros aún.</td></tr>
              )}
              {progress.map(p => (
                <tr key={p._id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-gray-800">{p.collaboratorUserId.slice(0, 12)}…</td>
                  <td className="px-4 py-3 text-gray-700">{p.courseTitle ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{p.courseSkillLabel ?? p.courseSkill ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${p.courseType === 'premium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                      {p.courseType === 'premium' ? 'Premium' : 'Gratuito'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {p.status === 'completed' ? 'Completado' : 'En progreso'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{new Date(p.createdAt).toLocaleDateString('es-MX')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── TAB: COMPLETADOS ── */}
      {tab === 'Completados' && (
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Colaborador', 'Curso', 'Habilidad', 'Fecha finalización', 'Insignia'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {progress.filter(p => p.status === 'completed').length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">Sin completados aún.</td></tr>
              )}
              {progress.filter(p => p.status === 'completed').map(p => (
                <tr key={p._id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-gray-800">{p.collaboratorUserId.slice(0, 12)}…</td>
                  <td className="px-4 py-3 text-gray-700">{p.courseTitle ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{p.courseSkillLabel ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">{p.completedAt ? new Date(p.completedAt).toLocaleDateString('es-MX') : '—'}</td>
                  <td className="px-4 py-3">
                    {p.badgeUrl && <img src={p.badgeUrl} alt="insignia" className="h-8 w-8 rounded-full object-cover border-2 border-amber-300" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── TAB: PAGOS PENDIENTES ── */}
      {tab === 'Pagos pendientes' && (
        <div className="space-y-3">
          {purchases.length === 0 && (
            <div className="rounded-xl bg-white border border-dashed border-gray-200 p-10 text-center">
              <DollarSign size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-400">No hay solicitudes de pago.</p>
            </div>
          )}
          {purchases.map(p => (
            <div key={p._id} className="rounded-xl bg-white border border-gray-100 p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${PAY_STATUS[p.status]?.color ?? 'bg-gray-100 text-gray-500'}`}>
                      {PAY_STATUS[p.status]?.label ?? p.status}
                    </span>
                    <span className="text-[11px] text-gray-400">{p.method === 'transfer' ? 'Transferencia' : 'Stripe'}</span>
                  </div>
                  <p className="font-semibold text-gray-900 text-sm">{p.collaboratorName ?? p.collaboratorUserId}</p>
                  <p className="text-xs text-gray-500">{p.courseTitle ?? p.courseId}</p>
                  <p className="mt-1 text-xs text-gray-400">
                    Monto: <strong>${p.amount?.toLocaleString('es-MX')} MXN</strong> · {new Date(p.createdAt).toLocaleDateString('es-MX')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewPurchase(p)}
                    className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50"
                  >
                    Ver detalle
                  </button>
                  {p.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleValidatePurchase(p, 'paid')}
                        className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold bg-green-500 text-white hover:bg-green-600"
                      >
                        <Check size={13} /> Marcar pagado
                      </button>
                      <button
                        onClick={() => handleValidatePurchase(p, 'rejected')}
                        className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold bg-red-500 text-white hover:bg-red-600"
                      >
                        <Ban size={13} /> Rechazar
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── MODAL: FORMULARIO CURSO ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">{editing ? 'Editar curso' : 'Crear curso'}</h2>
              <button onClick={() => setShowForm(false)} className="rounded-lg p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Nombre */}
              <div>
                <label className={labelCls}>Nombre del curso <span className="text-red-500">*</span></label>
                <input className={inputCls} value={form.title} onChange={e => set('title', e.target.value)} placeholder="Ej. Comunicación Efectiva en el Trabajo" />
              </div>
              {/* Descripción */}
              <div>
                <label className={labelCls}>Descripción</label>
                <textarea className={inputCls} rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe el objetivo del curso…" />
              </div>
              {/* Habilidad */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Habilidad relacionada <span className="text-red-500">*</span></label>
                  <select className={inputCls} value={form.skill} onChange={e => handleSkillChange(e.target.value)}>
                    <option value="">Seleccionar habilidad…</option>
                    {EVALUATION_ASPECTS.map(a => (
                      <option key={a.key} value={a.key}>{a.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Tipo de curso</label>
                  <select className={inputCls} value={form.type} onChange={e => set('type', e.target.value as 'free' | 'premium')}>
                    <option value="free">Gratuito</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
              </div>
              {/* Precio + Estado */}
              <div className="grid grid-cols-2 gap-4">
                {form.type === 'premium' && (
                  <div>
                    <label className={labelCls}>Costo (MXN)</label>
                    <input className={inputCls} type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)} placeholder="Ej. 499" />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Estado</label>
                  <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value as 'active' | 'inactive')}>
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
              </div>
              {/* Curso premium relacionado (para cursos free) */}
              {form.type === 'free' && premiumCourses.length > 0 && (
                <div>
                  <label className={labelCls}>Curso premium relacionado (opcional)</label>
                  <select className={inputCls} value={form.relatedPremiumId} onChange={e => set('relatedPremiumId', e.target.value)}>
                    <option value="">Ninguno</option>
                    {premiumCourses.map(c => (
                      <option key={c._id} value={c._id}>{c.title}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* Portada */}
              <div>
                <label className={labelCls}>Imagen / Portada del curso</label>
                <div className="flex items-center gap-3">
                  {form.coverUrl && (
                    <img src={form.coverUrl} alt="portada" className="h-16 w-16 rounded-lg object-cover border border-gray-200" />
                  )}
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-500 hover:bg-gray-50">
                    {uploadingCover ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" /> Subiendo…</> : <><ImageIcon size={16} /> Subir imagen</>}
                    <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploadingCover} />
                  </label>
                </div>
              </div>
              {/* HTML */}
              <div>
                <label className={labelCls}>Contenido HTML del curso</label>
                <textarea
                  className={`${inputCls} font-mono text-xs`}
                  rows={8}
                  value={form.htmlContent}
                  onChange={e => set('htmlContent', e.target.value)}
                  placeholder="Pega o escribe el contenido HTML del curso aquí…"
                />
                <p className="mt-1 text-[11px] text-gray-400">El HTML se renderizará de forma segura dentro del curso. Usa estilos inline para garantizar compatibilidad.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button onClick={() => setShowForm(false)} className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
              >
                {saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <Save size={15} />}
                {editing ? 'Guardar cambios' : 'Crear curso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: VER CONTENIDO HTML ── */}
      {viewCourse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 truncate">{viewCourse.title}</h2>
              <button onClick={() => setViewCourse(null)} className="rounded-lg p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-6">
              {viewCourse.coverUrl && (
                <img src={viewCourse.coverUrl} alt="portada" className="mb-4 w-full rounded-xl object-cover max-h-48" />
              )}
              <div
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: viewCourse.htmlContent ?? '<p class="text-gray-400">Sin contenido HTML cargado.</p>' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: DETALLE DE PAGO ── */}
      {viewPurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Detalle de pago</h2>
              <button onClick={() => setViewPurchase(null)} className="rounded-lg p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              {[
                ['Usuario', viewPurchase.collaboratorName ?? viewPurchase.collaboratorUserId],
                ['Curso', viewPurchase.courseTitle ?? viewPurchase.courseId],
                ['Monto', `$${viewPurchase.amount?.toLocaleString('es-MX')} MXN`],
                ['Método', viewPurchase.method === 'transfer' ? 'Transferencia bancaria' : 'Stripe'],
                ['Estado', PAY_STATUS[viewPurchase.status]?.label ?? viewPurchase.status],
                ['Fecha de solicitud', new Date(viewPurchase.createdAt).toLocaleString('es-MX')],
                ...(viewPurchase.validatedAt ? [['Fecha de acreditación', new Date(viewPurchase.validatedAt).toLocaleString('es-MX')]] : []),
                ...(viewPurchase.validatedBy ? [['Validado por (admin)', viewPurchase.validatedBy.slice(0, 16) + '…']] : []),
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-gray-500">{k}</span>
                  <span className="font-medium text-gray-800">{v}</span>
                </div>
              ))}
            </div>
            {viewPurchase.status === 'pending' && (
              <div className="flex gap-3 border-t border-gray-100 px-6 py-4">
                <button
                  onClick={() => handleValidatePurchase(viewPurchase, 'rejected')}
                  className="flex-1 flex items-center justify-center gap-1 rounded-lg border border-red-200 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  <Ban size={14} /> Rechazar
                </button>
                <button
                  onClick={() => handleValidatePurchase(viewPurchase, 'paid')}
                  className="flex-1 flex items-center justify-center gap-1 rounded-lg bg-green-500 py-2 text-sm font-semibold text-white hover:bg-green-600"
                >
                  <Check size={14} /> Marcar como pagado
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: HISTORIAL DE PAGOS ── */}
      {tab === 'Historial de pagos' && (
        <div>
          {/* Filtros rápidos */}
          <div className="mb-4 flex flex-wrap gap-2">
            {(['Todos', 'Pendiente', 'Pagado', 'Rechazado'] as const).map(f => (
              <button
                key={f}
                onClick={() => setPayFilter(f)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  payFilter === f ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Colaborador', 'Curso', 'Método', 'Monto', 'Estado', 'Fecha solicitud', 'Fecha acreditación'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredPurchaseHistory.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">Sin registros.</td></tr>
                )}
                {filteredPurchaseHistory.map(p => (
                  <tr key={p._id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{p.collaboratorName ?? '—'}</p>
                      <p className="text-[11px] text-gray-400">{p.collaboratorEmail ?? ''}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.courseTitle ?? p.courseId}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        p.method === 'transfer' ? 'bg-green-50 text-green-700' : 'bg-indigo-50 text-indigo-700'
                      }`}>
                        {p.method === 'transfer' ? 'Transferencia' : 'Stripe'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-800">${p.amount?.toLocaleString('es-MX')} MXN</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${PAY_STATUS[p.status]?.color ?? 'bg-gray-100 text-gray-500'}`}>
                        {PAY_STATUS[p.status]?.label ?? p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{new Date(p.createdAt).toLocaleDateString('es-MX')}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">{p.validatedAt ? new Date(p.validatedAt).toLocaleDateString('es-MX') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CONFIRMAR ELIMINAR ── */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center">
            <AlertCircle size={40} className="mx-auto mb-3 text-red-400" />
            <h3 className="text-lg font-bold text-gray-900 mb-1">Eliminar curso</h3>
            <p className="text-sm text-gray-500 mb-5">¿Estás seguro de que deseas eliminar este curso? Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={() => handleDelete(confirmDelete)} className="flex-1 rounded-lg bg-red-500 py-2 text-sm font-semibold text-white hover:bg-red-600">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
